import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { autoSuggestMappingsAPI, getDepartmentsListAPI, getOrgMappingsAPI, saveOrgMappingsAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import { companyUrl } from '../../../utils/mhNav';
import styles from './OrgMapping.module.scss';

export default function OrgMapping() {
  const [rows, setRows] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, d] = await Promise.all([getOrgMappingsAPI(), getDepartmentsListAPI()]);
      setRows(m.data || []);
      setDeptOpts(
        (d.data || []).filter((x) => !x.archived).map((x) => ({ value: x.id, label: x.name }))
      );
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setFinal = (rowId, opt) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              finalDepartmentId: opt?.value || null,
              finalDepartmentName: opt?.label || null,
              status: opt ? 'mapped' : r.status === 'conflict' ? 'conflict' : 'unmapped',
            }
          : r
      )
    );
  };

  const suggest = async () => {
    try {
      const res = await autoSuggestMappingsAPI();
      setRows(res.data || []);
      toast.success('Suggestions refreshed');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveAll = async () => {
    try {
      await saveOrgMappingsAPI(rows);
      toast.success(`${rows.length} group mappings saved`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: 'External group',
        selector: (r) => r.externalGroup,
        grow: 2,
      },
      {
        name: 'Provider',
        selector: (r) => r.provider,
        width: '100px',
      },
      {
        name: 'Suggested',
        selector: (r) => r.suggestedDepartment || '—',
      },
      {
        name: 'Final mapping',
        grow: 2,
        cell: (r) => (
          <Select
            className="flex-grow-1"
            options={deptOpts}
            value={deptOpts.find((o) => o.value === r.finalDepartmentId) || null}
            onChange={(opt) => setFinal(r.id, opt)}
            placeholder="Select department…"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        ),
      },
      {
        name: 'Status',
        width: '120px',
        cell: (r) => {
          const v = r.status === 'mapped' ? 'success' : r.status === 'conflict' ? 'danger' : 'warning';
          return <Badge bg={v}>{r.status}</Badge>;
        },
      },
    ],
    [deptOpts]
  );

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Org mapping" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Departments', path: companyUrl('departments') }, { label: 'Org mapping' }]} />
        <LoadingSkeleton rows={6} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Org mapping" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Departments', path: companyUrl('departments') }, { label: 'Org mapping' }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Org mapping"
        breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Departments', path: companyUrl('departments') }, { label: 'Org mapping' }]}
        secondaryActions={[
          { label: 'Auto suggest', onClick: suggest, variant: 'outline-primary' },
          { label: 'Save all', onClick: saveAll, variant: 'primary' },
        ]}
      />

      {!rows.length ? (
        <EmptyState icon={AlertTriangle} title="No mappings" description="No directory groups to map." actionLabel="Reload" onAction={load} />
      ) : (
        <DataTableWrapper
          columns={columns}
          data={rows}
          loading={loading}
          showSearch={false}
          keyField="id"
          customTableProps={{
            conditionalRowStyles: [
              {
                when: (r) => r.status === 'unmapped',
                style: { backgroundColor: 'rgba(255, 193, 7, 0.15)' },
              },
            ],
          }}
        />
      )}
    </div>
  );
}
